import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';

const CURSOR_IMAGE = "https://customer-assets.emergentagent.com/job_e7d2c8af-0408-4ea0-8a8c-154f63bf4f4e/artifacts/4vr2fcpi_seta.png";

const CustomCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isPointer, setIsPointer] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [trail, setTrail] = useState([]);
  const [clickEffects, setClickEffects] = useState([]);

  const updateMousePosition = useCallback((e) => {
    const newPosition = { x: e.clientX, y: e.clientY };
    setMousePosition(newPosition);
    
    // Adiciona posição ao trail
    setTrail(prev => {
      const newTrail = [...prev, { ...newPosition, id: Date.now() }];
      return newTrail.slice(-6);
    });
  }, []);

  const updateCursorType = useCallback((e) => {
    const target = e.target;
    const isClickable = 
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.tagName === 'INPUT' ||
      target.closest('button') ||
      target.closest('a') ||
      window.getComputedStyle(target).cursor === 'pointer';
    setIsPointer(isClickable);
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsClicking(true);
    
    // Adiciona efeito de clique
    const newEffect = {
      id: Date.now(),
      x: e.clientX,
      y: e.clientY,
    };
    setClickEffects(prev => [...prev, newEffect]);
    
    // Remove o efeito após a animação
    setTimeout(() => {
      setClickEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
    }, 600);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsClicking(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('mouseover', updateCursorType);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mouseover', updateCursorType);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updateMousePosition, updateCursorType, handleMouseDown, handleMouseUp]);

  // Limpa trail antigo
  useEffect(() => {
    const interval = setInterval(() => {
      setTrail(prev => prev.filter(point => Date.now() - point.id < 250));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Não mostrar em dispositivos touch
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  if (isTouchDevice) return null;

  return (
    <>
      {/* Esconde o cursor padrão */}
      <style>{`
        * {
          cursor: none !important;
        }
      `}</style>

      {/* Efeitos de clique - ondas expandindo */}
      <AnimatePresence>
        {clickEffects.map((effect) => (
          <React.Fragment key={effect.id}>
            {/* Onda 1 */}
            <motion.div
              initial={{ scale: 0, opacity: 0.8 }}
              animate={{ scale: 2.5, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="fixed pointer-events-none z-[9996]"
              style={{
                left: effect.x - 25,
                top: effect.y - 25,
              }}
            >
              <div 
                className="w-12 h-12 rounded-full"
                style={{
                  border: '2px solid rgba(255, 150, 200, 0.6)',
                  background: 'radial-gradient(circle, rgba(255, 200, 100, 0.3) 0%, transparent 70%)',
                }}
              />
            </motion.div>
            
            {/* Onda 2 */}
            <motion.div
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: 2, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: 0.1 }}
              className="fixed pointer-events-none z-[9996]"
              style={{
                left: effect.x - 20,
                top: effect.y - 20,
              }}
            >
              <div 
                className="w-10 h-10 rounded-full"
                style={{
                  border: '2px solid rgba(100, 200, 255, 0.5)',
                }}
              />
            </motion.div>

            {/* Partículas */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  scale: 1, 
                  opacity: 1,
                  x: effect.x,
                  y: effect.y,
                }}
                animate={{ 
                  scale: 0,
                  opacity: 0,
                  x: effect.x + Math.cos(i * 60 * Math.PI / 180) * 50,
                  y: effect.y + Math.sin(i * 60 * Math.PI / 180) * 50,
                }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="fixed pointer-events-none z-[9997]"
              >
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{
                    background: i % 2 === 0 
                      ? 'linear-gradient(135deg, #FF96C8, #FFD700)' 
                      : 'linear-gradient(135deg, #64C8FF, #FF96C8)',
                  }}
                />
              </motion.div>
            ))}
          </React.Fragment>
        ))}
      </AnimatePresence>

      {/* Trail - rastro com a imagem menor */}
      {trail.map((point, index) => (
        <motion.div
          key={point.id}
          initial={{ opacity: 0.4, scale: 0.5 }}
          animate={{ opacity: 0, scale: 0.2 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed pointer-events-none z-[9998]"
          style={{
            left: point.x,
            top: point.y,
          }}
        >
          <img 
            src={CURSOR_IMAGE} 
            alt="" 
            style={{
              width: 20 + (index * 2),
              height: 'auto',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              filter: `blur(${2 - index * 0.2}px)`,
            }}
          />
        </motion.div>
      ))}

      {/* Cursor principal */}
      <motion.div
        className="fixed pointer-events-none z-[9999]"
        animate={{
          x: mousePosition.x,
          y: mousePosition.y,
          scale: isClicking ? 0.8 : isPointer ? 1.2 : 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 800,
          damping: 35,
        }}
      >
        <motion.img
          src={CURSOR_IMAGE}
          alt=""
          animate={{
            rotate: isClicking ? -55 : isPointer ? -35 : -45,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 20,
          }}
          style={{
            width: 40,
            height: 'auto',
            transform: 'translate(-20%, -20%)',
            filter: isClicking
              ? 'drop-shadow(0 0 15px rgba(255, 200, 100, 0.8)) brightness(1.2)'
              : isPointer 
                ? 'drop-shadow(0 0 10px rgba(255, 100, 200, 0.5))' 
                : 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
          }}
        />
      </motion.div>

      {/* Brilho extra no hover */}
      {isPointer && !isClicking && (
        <motion.div
          className="fixed pointer-events-none z-[9997]"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: 1,
            x: mousePosition.x - 25,
            y: mousePosition.y - 25,
          }}
          transition={{
            opacity: {
              duration: 1,
              repeat: Infinity,
            },
            scale: {
              type: 'spring',
              stiffness: 500,
            },
          }}
        >
          <div 
            className="w-12 h-12 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255, 150, 200, 0.4) 0%, transparent 70%)',
            }}
          />
        </motion.div>
      )}
    </>
  );
};

export default CustomCursor;
